import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { dataTableHeadClass } from '../../components/DataTablePanel';
import { TableIconButton } from '../../components/TableIconAction';
import {
  Badge,
  Button,
  Callout,
  CoinIcon,
  DocumentTextIcon,
  GripVerticalIcon,
  ListEmptyState,
  LoadingSpinner,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  SearchIcon,
  SegmentedControl,
  TableIcon,
  TextInput,
  TrashIcon,
  UndoIcon,
  XIcon,
  useConfirm,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { InvoiceListItem } from '../../types/invoice';
import { PaymentListItem } from '../../types/payment';
import {
  addBuckets,
  bucketsFromTotals,
  bucketsTotal,
  consumeSameThenCross,
  fromCents,
  pairKey,
  type MoneyBuckets,
} from '../../utils/allocationMatch';
import { formatDate, formatMoney } from '../../utils/format';
import { cn } from '../../lib/utils';

type ColumnFilter = 'open' | 'closed' | 'all';
type LinkFilter = 'all' | 'existing' | 'new';
type DragKind = 'invoice' | 'payment';

type PendingLink = {
  paymentId: number;
  invoiceId: number;
  invoiceAmount: string;
  cashAmount: string;
  total: string;
};

type DisplayLink = PendingLink & {
  key: string;
  source: 'existing' | 'new';
  invoiceNo: string;
  paymentDate: string;
  paymentLabel: string;
};

const PAGE_SIZE = 100;
const DRAG_MIME = 'application/x-balanceflow-match';

export function CustomerMatchBoardModal({
  isOpen,
  customerId,
  onClose,
  onApplied,
}: {
  isOpen: boolean;
  customerId: number;
  onClose: () => void;
  onApplied: () => void | Promise<void>;
}) {
  const labels = t().customers;
  const confirm = useConfirm();
  const dragRef = useRef<{ kind: DragKind; id: number } | null>(null);
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [payments, setPayments] = useState<PaymentListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState<ColumnFilter>('open');
  const [paymentFilter, setPaymentFilter] = useState<ColumnFilter>('open');
  const [linkFilter, setLinkFilter] = useState<LinkFilter>('all');
  const [pending, setPending] = useState<PendingLink[]>([]);
  const [unlinked, setUnlinked] = useState<Set<string>>(new Set());
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null,
  );
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(
    null,
  );
  const [dropTarget, setDropTarget] = useState<{
    kind: DragKind;
    id: number;
  } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      setWarning(null);
      setNotice(null);
      setPending([]);
      setUnlinked(new Set());
      setSelectedInvoiceId(null);
      setSelectedPaymentId(null);
      setSearch('');
      setInvoiceFilter('open');
      setPaymentFilter('open');
      setLinkFilter('all');
      try {
        const [invoiceRows, paymentRows] = await Promise.all([
          fetchAllPages((page) =>
            api.getInvoices({
              customerId,
              page,
              pageSize: PAGE_SIZE,
              sortBy: 'dueDate',
              sortDir: 'asc',
            }),
          ),
          fetchAllPages((page) =>
            api.getPayments({
              customerId,
              page,
              pageSize: PAGE_SIZE,
              sortBy: 'paymentDate',
              sortDir: 'asc',
            }),
          ),
        ]);
        if (!cancelled) {
          setInvoices(invoiceRows);
          setPayments(paymentRows);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, customerId]);

  const invoiceById = useMemo(() => {
    const map = new Map<number, InvoiceListItem>();
    for (const invoice of invoices) map.set(invoice.id, invoice);
    return map;
  }, [invoices]);

  const paymentById = useMemo(() => {
    const map = new Map<number, PaymentListItem>();
    for (const payment of payments) map.set(payment.id, payment);
    return map;
  }, [payments]);

  const remaining = useMemo(
    () => computeRemaining(invoices, payments, pending, unlinked),
    [invoices, payments, pending, unlinked],
  );

  const displayLinks = useMemo(() => {
    const rows: DisplayLink[] = [];
    for (const payment of payments) {
      for (const allocation of payment.allocations) {
        const key = pairKey(payment.id, allocation.invoiceId);
        if (unlinked.has(key)) continue;
        rows.push({
          key,
          source: 'existing',
          paymentId: payment.id,
          invoiceId: allocation.invoiceId,
          invoiceNo: allocation.invoiceNo || `#${allocation.invoiceId}`,
          paymentDate: payment.paymentDate,
          paymentLabel: paymentLabel(payment),
          invoiceAmount: allocation.invoiceAmount,
          cashAmount: allocation.cashAmount,
          total: allocation.total,
        });
      }
    }
    for (const link of pending) {
      const invoice = invoiceById.get(link.invoiceId);
      const payment = paymentById.get(link.paymentId);
      rows.push({
        key: `new:${pairKey(link.paymentId, link.invoiceId)}:${link.total}`,
        source: 'new',
        paymentId: link.paymentId,
        invoiceId: link.invoiceId,
        invoiceNo: invoice?.invoiceNo || `#${link.invoiceId}`,
        paymentDate: payment?.paymentDate || '',
        paymentLabel: payment ? paymentLabel(payment) : `PAY-${link.paymentId}`,
        invoiceAmount: link.invoiceAmount,
        cashAmount: link.cashAmount,
        total: link.total,
      });
    }
    return rows;
  }, [payments, pending, unlinked, invoiceById, paymentById]);

  const query = search.trim().toLowerCase();

  const visibleInvoices = invoices.filter((invoice) => {
    const live = remaining.invoices.get(invoice.id) ?? { invoice: 0, cash: 0 };
    const isOpenItem = bucketsTotal(live) > 0;
    if (invoiceFilter === 'open' && !isOpenItem) return false;
    if (invoiceFilter === 'closed' && isOpenItem) return false;
    if (!query) return true;
    return (
      invoice.invoiceNo.toLowerCase().includes(query) ||
      formatDate(invoice.dueDate).includes(query)
    );
  });

  const visiblePayments = payments.filter((payment) => {
    const live = remaining.payments.get(payment.id) ?? { invoice: 0, cash: 0 };
    const isOpenItem = bucketsTotal(live) > 0;
    if (paymentFilter === 'open' && !isOpenItem) return false;
    if (paymentFilter === 'closed' && isOpenItem) return false;
    if (!query) return true;
    const label = paymentLabel(payment).toLowerCase();
    const related = payment.allocations
      .map((row) => row.invoiceNo)
      .join(' ')
      .toLowerCase();
    return (
      label.includes(query) ||
      related.includes(query) ||
      (payment.description || '').toLowerCase().includes(query)
    );
  });

  const visibleLinks = displayLinks.filter((row) => {
    if (linkFilter === 'existing' && row.source !== 'existing') return false;
    if (linkFilter === 'new' && row.source !== 'new') return false;
    if (!query) return true;
    return (
      row.invoiceNo.toLowerCase().includes(query) ||
      row.paymentLabel.toLowerCase().includes(query)
    );
  });

  const dirty = pending.length > 0 || unlinked.size > 0;

  function tryMatch(invoiceId: number, paymentId: number) {
    const invoiceLive = remaining.invoices.get(invoiceId);
    const paymentLive = remaining.payments.get(paymentId);
    if (!invoiceLive || !paymentLive) return;
    const { take } = consumeSameThenCross(paymentLive, invoiceLive);
    if (bucketsTotal(take) <= 0) {
      setWarning(labels.matchBoardNoOverlap);
      return;
    }
    setNotice(null);
    setError(null);
    setWarning(null);
    setPending((rows) => [
      ...rows,
      {
        paymentId,
        invoiceId,
        invoiceAmount: fromCents(take.invoice),
        cashAmount: fromCents(take.cash),
        total: fromCents(take.invoice + take.cash),
      },
    ]);
    setSelectedInvoiceId(null);
    setSelectedPaymentId(null);
  }

  function onInvoiceActivate(invoiceId: number) {
    if (selectedPaymentId != null) {
      tryMatch(invoiceId, selectedPaymentId);
      return;
    }
    setSelectedInvoiceId((current) =>
      current === invoiceId ? null : invoiceId,
    );
  }

  function onPaymentActivate(paymentId: number) {
    if (selectedInvoiceId != null) {
      tryMatch(selectedInvoiceId, paymentId);
      return;
    }
    setSelectedPaymentId((current) =>
      current === paymentId ? null : paymentId,
    );
  }

  function readDrag(event: React.DragEvent) {
    const fromRef = dragRef.current;
    const raw =
      event.dataTransfer.getData(DRAG_MIME) ||
      event.dataTransfer.getData('text/plain');
    if (!raw) return fromRef;
    try {
      return JSON.parse(raw) as { kind: DragKind; id: number };
    } catch {
      return fromRef;
    }
  }

  function handleDropOn(kind: DragKind, id: number, event: React.DragEvent) {
    event.preventDefault();
    setDropTarget(null);
    const dragged = readDrag(event);
    dragRef.current = null;
    if (!dragged || dragged.kind === kind) return;
    if (kind === 'invoice' && dragged.kind === 'payment') {
      tryMatch(id, dragged.id);
    }
    if (kind === 'payment' && dragged.kind === 'invoice') {
      tryMatch(dragged.id, id);
    }
  }

  async function handleApply() {
    if (!dirty) return;
    const ok = await confirm({
      title: labels.matchBoardApply,
      message: labels.matchBoardApplyConfirm,
      confirmText: labels.matchBoardApply,
      cancelText: t().common.cancel,
    });
    if (!ok) return;
    setApplying(true);
    setError(null);
    try {
      const result = await api.applyManualMatch({
        customerId,
        links: pending.map((row) => ({
          paymentId: row.paymentId,
          invoiceId: row.invoiceId,
        })),
        unlink: Array.from(unlinked).map((key) => {
          const [paymentId, invoiceId] = key.split(':').map(Number);
          return { paymentId, invoiceId };
        }),
      });
      const parts: string[] = [labels.matchBoardApplied];
      if (result.skipped > 0) {
        parts.push(`${t().reports.autoAllocateSkipped}: ${result.skipped}`);
      }
      setNotice(parts.join(' '));
      setPending([]);
      setUnlinked(new Set());
      await onApplied();
      const [invoiceRows, paymentRows] = await Promise.all([
        fetchAllPages((page) =>
          api.getInvoices({
            customerId,
            page,
            pageSize: PAGE_SIZE,
            sortBy: 'dueDate',
            sortDir: 'asc',
          }),
        ),
        fetchAllPages((page) =>
          api.getPayments({
            customerId,
            page,
            pageSize: PAGE_SIZE,
            sortBy: 'paymentDate',
            sortDir: 'asc',
          }),
        ),
      ]);
      setInvoices(invoiceRows);
      setPayments(paymentRows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setApplying(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-[92vw]"
      size="3xl"
    >
      <ModalHeader>
        <ModalTitle>{labels.matchBoardTitle}</ModalTitle>
      </ModalHeader>
      <ModalContent className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-4">
        <p className="shrink-0 text-sm text-ink-muted">{labels.matchBoardHint}</p>
        <div className="shrink-0">
          <TextInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={labels.matchBoardSearch}
            prefix={<SearchIcon className="h-4 w-4" />}
          />
        </div>
        {error ? (
          <Callout variant="danger" className="shrink-0">
            {error}
          </Callout>
        ) : null}
        {warning ? (
          <Callout variant="warning" className="shrink-0">
            {warning}
          </Callout>
        ) : null}
        {notice ? (
          <Callout variant="success" className="shrink-0">
            {notice}
          </Callout>
        ) : null}
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : (
          <>
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
              <BoardColumn
                title={labels.matchBoardInvoices}
                filter={invoiceFilter}
                onFilter={setInvoiceFilter}
                filterLabel={labels.invoiceStatusView}
              >
                {visibleInvoices.length === 0 ? (
                  <ListEmptyState title={labels.matchBoardEmptyInvoices} />
                ) : (
                  visibleInvoices.map((invoice) => {
                    const live =
                      remaining.invoices.get(invoice.id) ?? {
                        invoice: 0,
                        cash: 0,
                      };
                    const open = bucketsTotal(live) > 0;
                    return (
                      <BoardCard
                        key={invoice.id}
                        draggable={open && !applying}
                        selected={selectedInvoiceId === invoice.id}
                        dropActive={
                          dropTarget?.kind === 'invoice' &&
                          dropTarget.id === invoice.id
                        }
                        dimmed={!open}
                        onActivate={() => onInvoiceActivate(invoice.id)}
                        onDragStart={(event) =>
                          startDrag(event, dragRef, 'invoice', invoice.id)
                        }
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDropTarget({ kind: 'invoice', id: invoice.id });
                        }}
                        onDragLeave={() => setDropTarget(null)}
                        onDrop={(event) =>
                          handleDropOn('invoice', invoice.id, event)
                        }
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <Link
                              to={`/invoices/${invoice.id}`}
                              className="font-medium text-brand hover:underline"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {invoice.invoiceNo}
                            </Link>
                            <div className="text-xs text-ink-muted">
                              {formatDate(invoice.invoiceDate)} ·{' '}
                              {t().invoices.dueDate} {formatDate(invoice.dueDate)}
                            </div>
                          </div>
                          <DocumentTextIcon className="h-4 w-4 text-ink-faint" />
                        </div>
                        <BucketRow
                          label={labels.matchBoardRemaining}
                          buckets={live}
                        />
                      </BoardCard>
                    );
                  })
                )}
              </BoardColumn>

              <BoardColumn
                title={labels.matchBoardPayments}
                filter={paymentFilter}
                onFilter={setPaymentFilter}
                filterLabel={labels.matchBoardPayments}
              >
                {visiblePayments.length === 0 ? (
                  <ListEmptyState title={labels.matchBoardEmptyPayments} />
                ) : (
                  visiblePayments.map((payment) => {
                    const live =
                      remaining.payments.get(payment.id) ?? {
                        invoice: 0,
                        cash: 0,
                      };
                    const open = bucketsTotal(live) > 0;
                    return (
                      <BoardCard
                        key={payment.id}
                        draggable={open && !applying}
                        selected={selectedPaymentId === payment.id}
                        dropActive={
                          dropTarget?.kind === 'payment' &&
                          dropTarget.id === payment.id
                        }
                        dimmed={!open}
                        onActivate={() => onPaymentActivate(payment.id)}
                        onDragStart={(event) =>
                          startDrag(event, dragRef, 'payment', payment.id)
                        }
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDropTarget({ kind: 'payment', id: payment.id });
                        }}
                        onDragLeave={() => setDropTarget(null)}
                        onDrop={(event) =>
                          handleDropOn('payment', payment.id, event)
                        }
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <Link
                              to={`/payments/${payment.id}`}
                              className="font-medium text-brand hover:underline"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {paymentLabel(payment)}
                            </Link>
                            <div className="text-xs text-ink-muted">
                              {formatDate(payment.paymentDate)}
                              {payment.description
                                ? ` · ${payment.description}`
                                : ''}
                            </div>
                          </div>
                          <CoinIcon className="h-4 w-4 text-ink-faint" />
                        </div>
                        <BucketRow
                          label={labels.matchBoardUnallocated}
                          buckets={live}
                        />
                      </BoardCard>
                    );
                  })
                )}
              </BoardColumn>
            </div>

            <section className="flex max-h-56 min-h-[9rem] shrink-0 flex-col overflow-hidden rounded-lg border border-line">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
                <h3 className="text-sm font-semibold">{labels.matchBoardLinks}</h3>
                <SegmentedControl
                  size="sm"
                  value={linkFilter}
                  onChange={setLinkFilter}
                  aria-label={labels.matchBoardLinks}
                  options={[
                    {
                      id: 'all',
                      label: t().invoices.filters.all,
                      icon: <TableIcon className="h-3.5 w-3.5" />,
                      count: displayLinks.length,
                    },
                    {
                      id: 'existing',
                      label: labels.matchBoardExisting,
                      count: displayLinks.filter((row) => row.source === 'existing')
                        .length,
                    },
                    {
                      id: 'new',
                      label: labels.matchBoardNew,
                      count: displayLinks.filter((row) => row.source === 'new')
                        .length,
                    },
                  ]}
                />
              </div>
              <div className="min-h-0 flex-1 overflow-auto">
                {visibleLinks.length === 0 ? (
                  <ListEmptyState title={labels.matchBoardEmptyLinks} />
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className={dataTableHeadClass}>
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">
                          {t().payments.invoice}
                        </th>
                        <th className="px-3 py-2 text-left font-semibold">
                          {t().payments.title}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t().financial.invoice}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t().financial.cash}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t().financial.grandTotal}
                        </th>
                        <th className="px-3 py-2 text-left font-semibold">
                          {t().invoices.status}
                        </th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLinks.map((row) => (
                        <tr key={row.key} className="border-t border-line">
                          <td className="px-3 py-2">
                            <Link
                              to={`/invoices/${row.invoiceId}`}
                              className="text-brand hover:underline"
                            >
                              {row.invoiceNo}
                            </Link>
                          </td>
                          <td className="px-3 py-2">
                            <Link
                              to={`/payments/${row.paymentId}`}
                              className="text-brand hover:underline"
                            >
                              {row.paymentLabel}
                            </Link>
                            <div className="text-xs text-ink-muted">
                              {formatDate(row.paymentDate)}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatMoney(row.invoiceAmount)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatMoney(row.cashAmount)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">
                            {formatMoney(row.total)}
                          </td>
                          <td className="px-3 py-2">
                            <Badge
                              variant={
                                row.source === 'new' ? 'info' : 'secondary'
                              }
                            >
                              {row.source === 'new'
                                ? labels.matchBoardNew
                                : labels.matchBoardExisting}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {row.source === 'new' ? (
                              <TableIconButton
                                label={labels.matchBoardUndo}
                                variant="muted"
                                onClick={() =>
                                  setPending((rows) =>
                                    rows.filter(
                                      (item) =>
                                        !(
                                          item.paymentId === row.paymentId &&
                                          item.invoiceId === row.invoiceId &&
                                          item.total === row.total
                                        ),
                                    ),
                                  )
                                }
                              >
                                <UndoIcon className="h-4 w-4" />
                              </TableIconButton>
                            ) : (
                              <TableIconButton
                                label={labels.matchBoardUnlink}
                                variant="danger"
                                onClick={() =>
                                  setUnlinked((current) => {
                                    const next = new Set(current);
                                    next.add(
                                      pairKey(row.paymentId, row.invoiceId),
                                    );
                                    return next;
                                  })
                                }
                              >
                                <TrashIcon className="h-4 w-4" />
                              </TableIconButton>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </>
        )}
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          <XIcon className="h-4 w-4" />
          {t().common.close}
        </Button>
        <Button
          type="button"
          loading={applying}
          disabled={!dirty || applying || loading}
          onClick={() => void handleApply()}
        >
          {labels.matchBoardApply}
          {dirty ? ` (${pending.length + unlinked.size})` : ''}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function BoardColumn({
  title,
  filter,
  onFilter,
  filterLabel,
  children,
}: {
  title: string;
  filter: ColumnFilter;
  onFilter: (value: ColumnFilter) => void;
  filterLabel: string;
  children: React.ReactNode;
}) {
  const labels = t().customers;
  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-line">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <SegmentedControl
          size="sm"
          value={filter}
          onChange={onFilter}
          aria-label={filterLabel}
          options={[
            { id: 'open', label: labels.matchBoardOpen },
            { id: 'closed', label: labels.matchBoardClosed },
            {
              id: 'all',
              label: t().invoices.filters.all,
              icon: <TableIcon className="h-3.5 w-3.5" />,
            },
          ]}
        />
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-auto p-2">{children}</div>
    </section>
  );
}

function BoardCard({
  children,
  draggable,
  selected,
  dropActive,
  dimmed,
  onActivate,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  children: React.ReactNode;
  draggable: boolean;
  selected: boolean;
  dropActive: boolean;
  dimmed: boolean;
  onActivate: () => void;
  onDragStart: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={draggable}
      onClick={onActivate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate();
        }
      }}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'w-full rounded-lg border px-3 py-2 text-left transition-colors',
        selected
          ? 'border-brand bg-brand/5'
          : 'border-line bg-surface-card hover:bg-surface-muted',
        dropActive && 'ring-2 ring-brand',
        dimmed && 'opacity-60',
        draggable && 'cursor-grab active:cursor-grabbing',
      )}
    >
      <div className="mb-1 flex justify-end">
        <GripVerticalIcon className="h-3.5 w-3.5 text-ink-faint" />
      </div>
      {dropActive ? (
        <div className="mb-2 text-xs font-medium text-brand">
          {t().customers.matchBoardDropHere}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function BucketRow({
  label,
  buckets,
}: {
  label: string;
  buckets: MoneyBuckets;
}) {
  return (
    <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
      <div>
        <div className="text-ink-muted">{t().financial.invoice}</div>
        <div className="tabular-nums">{formatMoney(fromCents(buckets.invoice))}</div>
      </div>
      <div>
        <div className="text-ink-muted">{t().financial.cash}</div>
        <div className="tabular-nums">{formatMoney(fromCents(buckets.cash))}</div>
      </div>
      <div>
        <div className="text-ink-muted">{label}</div>
        <div className="tabular-nums font-medium">
          {formatMoney(fromCents(bucketsTotal(buckets)))}
        </div>
      </div>
    </div>
  );
}

function paymentLabel(payment: PaymentListItem) {
  return `PAY-${payment.id}`;
}

function startDrag(
  event: React.DragEvent,
  dragRef: React.MutableRefObject<{ kind: DragKind; id: number } | null>,
  kind: DragKind,
  id: number,
) {
  const payload = { kind, id };
  dragRef.current = payload;
  const raw = JSON.stringify(payload);
  event.dataTransfer.setData(DRAG_MIME, raw);
  event.dataTransfer.setData('text/plain', raw);
  event.dataTransfer.effectAllowed = 'link';
}

function computeRemaining(
  invoices: InvoiceListItem[],
  payments: PaymentListItem[],
  pending: PendingLink[],
  unlinked: Set<string>,
) {
  const invoiceMap = new Map<number, MoneyBuckets>();
  const paymentMap = new Map<number, MoneyBuckets>();

  for (const invoice of invoices) {
    invoiceMap.set(invoice.id, bucketsFromTotals(invoice.outstanding));
  }
  for (const payment of payments) {
    paymentMap.set(payment.id, bucketsFromTotals(payment.unallocated));
  }

  for (const payment of payments) {
    for (const allocation of payment.allocations) {
      const key = pairKey(payment.id, allocation.invoiceId);
      if (!unlinked.has(key)) continue;
      const take = bucketsFromTotals({
        invoice: allocation.invoiceAmount,
        cash: allocation.cashAmount,
      });
      const invoiceLive = invoiceMap.get(allocation.invoiceId);
      const paymentLive = paymentMap.get(payment.id);
      if (invoiceLive) {
        invoiceMap.set(allocation.invoiceId, addBuckets(invoiceLive, take));
      }
      if (paymentLive) {
        paymentMap.set(payment.id, addBuckets(paymentLive, take));
      }
    }
  }

  for (const link of pending) {
    const invoiceLive = invoiceMap.get(link.invoiceId);
    const paymentLive = paymentMap.get(link.paymentId);
    if (!invoiceLive || !paymentLive) continue;
    const { nextPay, nextInvoice } = consumeSameThenCross(
      paymentLive,
      invoiceLive,
    );
    invoiceMap.set(link.invoiceId, nextInvoice);
    paymentMap.set(link.paymentId, nextPay);
  }

  return { invoices: invoiceMap, payments: paymentMap };
}

async function fetchAllPages<T>(
  load: (page: number) => Promise<{
    data: T[];
    meta: { pageCount: number };
  }>,
) {
  const first = await load(1);
  const all = [...first.data];
  for (let page = 2; page <= first.meta.pageCount; page += 1) {
    const next = await load(page);
    all.push(...next.data);
  }
  return all;
}
